import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CTBReport } from "../../../utils/api/endpoints/reports/parseReports";
import useReports from "../../../utils/hooks/reports/useReports";
import Pagination from "../../../components/retests/utils/Pagination";
import { parseCategoryString } from "../../../utils/categoryUtils";
import toast from "react-hot-toast";
import apiInstance from "../../../utils/api/axios";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";

const ReportTable: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useUserCredentials();
  const [expandedTitleId, setExpandedTitleId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 8;
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateOrder, setDateOrder] = useState<'new' | 'old' | ''>('');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      const response = await apiInstance.get(`/v2/reports/export`, {
        params: { program_id: id },
        responseType: "blob"
      });

            const contentDisposition = response.headers ? response.headers["content-disposition"] : null;
      let filename = `vulnerabilities_export_${id}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success("Excel export downloaded successfully.");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export vulnerabilities. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Use the same API as the main Reports page, filtered by program id
  // Increase limit to fetch more reports for client-side filtering and pagination
  const { reports, isLoading } = useReports({ ...({ program: id || "", includeClosed: true } as any), limit: 1000 });

  const filteredReports = useMemo(() => {
    const list: CTBReport[] = reports || [];
    return list
      .filter(r =>
        (r.title || "").toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
      .filter(r => (severityFilter ? ((r.severity?.category || "").toLowerCase() === severityFilter.toLowerCase()) : true))
      .filter(r => (statusFilter ? ((r.state || r.retestStatus || r.status || "").toString().toLowerCase() === statusFilter.toLowerCase()) : true))
      .sort((a, b) => {
        if (!dateOrder) return 0;
        const da = new Date(a.submittedDate || "").getTime();
        const db = new Date(b.submittedDate || "").getTime();
        return dateOrder === 'new' ? db - da : da - db;
      });
  }, [reports, searchQuery, severityFilter, statusFilter, dateOrder]);

  const totalReports = filteredReports.length;
  const totalPages = Math.ceil(totalReports / reportsPerPage);
  
  // Ensure current page is valid after filtering
  const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  
  const indexOfLastReport = safePage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);

  if (isLoading)
    return <p className="text-center text-gray-500">Loading reports...</p>;

  return (
    <div className="container">
      {/* Outer white container for Reports */}
      <div className="bg-white rounded-lg shadow p-4">
        {/* Controls */}
        <div className="flex items-center gap-4 mb-4 w-full">
          <div className="relative flex-[2]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search reports"
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              value={searchQuery}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchQuery(e.target.value);
              }}
            />
          </div>

          <div className="relative flex-1">
            <select
              className="border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full pr-10 appearance-none bg-white text-sm"
              value={severityFilter}
              onChange={(e) => { setCurrentPage(1); setSeverityFilter(e.target.value); }}
            >
              <option value="">All Categories</option>
              {Array.from(new Set((reports || []).map(r => r.severity?.category))).filter(Boolean).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div className="relative flex-1">
            <select
              className="border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full pr-10 appearance-none bg-white text-sm"
              value={statusFilter}
              onChange={(e) => { setCurrentPage(1); setStatusFilter(e.target.value); }}
            >
              <option value="">All Status</option>
              {Array.from(new Set((reports || []).map(r => (r.state || r.retestStatus || r.status || "").toString()))).filter(Boolean).map(opt => (
                <option key={opt as string} value={opt as string}>{opt as string}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div className="relative flex-1">
            <select
              className="border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full pr-10 appearance-none bg-white text-sm"
              value={dateOrder}
              onChange={(e) => { setCurrentPage(1); setDateOrder(e.target.value as any); }}
            >
              <option value="">Sort by date</option>
              <option value="new">Newest first</option>
              <option value="old">Oldest first</option>
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {role && [
            UserRole.BUSINESS,
            UserRole.BUSINESS_ADMINISTRATOR,
            UserRole.ADMIN,
            UserRole.SUB_ADMIN,
            UserRole.ADMIN_MANAGER,
            UserRole.QA
          ].includes(role) && (
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-semibold transition-all duration-200 cursor-pointer flex-none border border-transparent shadow-sm ${
                isExporting
                  ? "bg-[#010D2C] bg-opacity-70 text-gray-400 cursor-not-allowed"
                  : "bg-[#010D2C] text-[#58CC02] hover:bg-opacity-90 active:scale-95"
              }`}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export
                </>
              )}
            </button>
          )}
        </div>

        <div className="overflow-hidden rounded-md border border-gray-200 shadow-md">
          <div className="max-h-[30rem] overflow-y-auto">
            <table className="min-w-full bg-white">
              <thead className="sticky top-0 z-10 bg-[#010D2C] text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-medium w-1/4">Report Title</th>
                  <th className="px-3 py-3 text-left font-medium w-1/4">Category</th>
                  <th className="px-4 py-3 text-left font-medium w-1/6">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Severity</th>
                  <th className="px-4 py-3 text-left font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="py-4 text-center text-gray-500">
                      Loading reports...
                    </td>
                  </tr>
                ) : currentReports.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-4 text-center text-gray-500">
                      No reports generated yet.
                    </td>
                  </tr>
                ) : (
                  currentReports.map(report => {

                    return (
                      <tr
                        key={report.id}
                        className="border-b hover:bg-gray-100"
                      >
                        <td className="px-4 py-3 w-1/4 align-top whitespace-normal break-words">
                          <span className="relative text-sm font-medium" style={{ color: '#010D2C' }} title={report.title}>
                            {report.title}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[13px] w-1/4 align-top whitespace-normal break-words">
                          {(() => {
                            const palette = ['#155E75', '#92400E', '#1E40AF'];
                            const color = palette[report.id % palette.length];
                            return (
                              <div
                                style={{
                                  color,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {parseCategoryString(report.category || "")}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-[13px] align-top" style={{ color: '#6B7280' }}>
                          {report.submittedDate || ""}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium capitalize text-gray-700">
                            {(report.retestStatus || report.state || report.status || "") as string}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          {(() => {
                            const sev = (report.severity?.category || "").toLowerCase();
                            const isInfo = sev === "none" || sev === "info" || sev === "informational";
                            const bg =
                              sev === "low"
                                ? "#22C55E"
                                : sev === "medium"
                                  ? "#F2B200"
                                  : sev === "high"
                                    ? "#EF9234"
                                    : sev === "critical"
                                      ? "#C9190BE5"
                                      : isInfo
                                        ? "#3B82F6"
                                        : "#9CA3AF";
                            const textColor = "#FFFFFF";
                            const displayLabel = isInfo ? "Info" : (report.severity?.category || "").charAt(0).toUpperCase() + (report.severity?.category || "").slice(1).toLowerCase();
                            return (
                              <span
                                className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium capitalize"
                                style={{ backgroundColor: bg, color: textColor }}
                              >
                                {displayLabel}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <button
                            className="text-blue-600 hover:underline font-medium text-sm"
                            onClick={() => navigate(`/dashboard/reports/${report.id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
        {/* Close bg-white container */}
      </div>
    </div>
  );
};

export default ReportTable;
